/**
 * PermisosPanel — GEN-EKB-013: mapa de permisos de «Mi empresa · KBs» (solo admin).
 * Una pantalla con qué perfil puede usar cada KB; desde ella el administrador concede
 * (perfil + para qué) y revoca. Denegación por defecto: un KB sin concesiones solo lo
 * ven el administrador y su titular. El kernel decide y revalida (R-PT-04).
 */

import { useCallback, useEffect, useState } from 'react'
import { concederPermiso, getMapaPermisos, revocarPermiso } from '../api/client.js'

const MENSAJES = {
  para_que_invalido: 'Di para qué se concede.',
  ya_concedido: 'Ese perfil ya tiene permiso sobre el KB.',
  perfil_invalido: 'Elige un perfil de la lista.',
  kb_retirado: 'El KB está retirado.',
  ekb_no_disponible: 'La base de conocimiento aún no está disponible en esta instalación.',
  forbidden: 'Solo el administrador gestiona los permisos.',
}
const mensaje = err => MENSAJES[err?.code] || err?.message || 'No se ha podido completar la operación.'
const fecha = iso => (iso ? new Date(iso).toLocaleDateString() : '')

// GEN-PX-020e: `kbIds` (opcional) limita la tabla y el selector a esos KBs (los de un área y sus subáreas); `areaNombre` rotula el título.
export default function PermisosPanel({ onVolver, kbIds = null, areaNombre = null }) {
  const [mapa, setMapa] = useState(null)
  const [kbId, setKbId] = useState('')
  const [perfil, setPerfil] = useState('')
  const [paraQue, setParaQue] = useState('')
  const [error, setError] = useState(null)

  const cargar = useCallback(async () => {
    try {
      setMapa(await getMapaPermisos())
    } catch (err) {
      setMapa({ error: mensaje(err) })
    }
  }, [])
  useEffect(() => { cargar() }, [cargar])

  async function conceder(e) {
    e.preventDefault()
    setError(null)
    try {
      await concederPermiso(kbId, perfil, paraQue.trim())
      setParaQue('')
      await cargar()
    } catch (err) {
      setError(mensaje(err))
    }
  }

  async function revocar(kb, p) {
    if (!window.confirm(`¿Quitar a «${p.perfil}» el permiso sobre «${kb.nombre}»?`)) return
    setError(null)
    try {
      await revocarPermiso(kb.id, p.perfil)
      await cargar()
    } catch (err) {
      setError(mensaje(err))
    }
  }

  const perfiles = mapa?.perfiles ?? []
  const todos = mapa?.kbs ?? []
  const kbs = kbIds ? todos.filter(k => kbIds.includes(k.id)) : todos

  return (
    <section className="mec-visor">
      <button type="button" className="btn btn--ghost" onClick={onVolver}>← KBs</button>
      <h2>{areaNombre ? `Permisos de «${areaNombre}»` : 'Mapa de permisos'}</h2>
      <p className="equipo-estado">
        Qué perfil puede usar cada KB. El administrador y el titular de cada KB lo ven siempre; el resto, solo si se concede.
      </p>
      {!mapa && <p className="equipo-estado">Cargando…</p>}
      {mapa?.error && <p className="equipo-estado" role="alert">{mapa.error}</p>}
      {error && <p className="equipo-estado" role="alert">{error}</p>}
      {mapa && !mapa.error && kbs.length === 0 && <p className="equipo-estado">{areaNombre ? 'Esta área no tiene KBs todavía.' : 'Todavía no hay KBs de empresa.'}</p>}

      {kbs.length > 0 && (
        <table className="kbs-tabla" aria-label="Mapa de permisos">
          <thead>
            <tr><th>KB</th><th>Titular</th>{perfiles.map(p => <th key={p.id}>{p.etiqueta}</th>)}</tr>
          </thead>
          <tbody>
            {kbs.map(kb => (
              <tr key={kb.id}>
                <td>{kb.nombre}</td>
                <td>{kb.titular?.display_name}</td>
                {perfiles.map(pf => {
                  const p = kb.permisos.find(x => x.perfil === pf.id)
                  return (
                    <td key={pf.id}>
                      {p ? (
                        <>
                          <span title={`${p.para_que} · ${p.concedido_por?.display_name ?? ''} · ${fecha(p.concedido_en)}`}>
                            Sí · {p.para_que}
                          </span>{' '}
                          <button type="button" className="btn btn--ghost" aria-label={`Revocar ${pf.etiqueta} en ${kb.nombre}`} onClick={() => revocar(kb, p)}>
                            Quitar
                          </button>
                        </>
                      ) : '—'}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {kbs.length > 0 && (
        <form className="equipo-nuevo" onSubmit={conceder}>
          <h3>Conceder el uso de un KB</h3>
          <label>
            KB
            <select required value={kbId} onChange={e => setKbId(e.target.value)}>
              <option value="">Elige un KB</option>
              {kbs.map(k => <option key={k.id} value={k.id}>{k.nombre}</option>)}
            </select>
          </label>
          <label>
            Perfil
            <select required value={perfil} onChange={e => setPerfil(e.target.value)}>
              <option value="">Elige un perfil</option>
              {perfiles.map(p => <option key={p.id} value={p.id}>{p.etiqueta}</option>)}
            </select>
          </label>
          <label>
            Para qué
            <input required maxLength={300} autoComplete="off" value={paraQue} onChange={e => setParaQue(e.target.value)} placeholder="Operación de compras" />
          </label>
          <div className="equipo-nuevo__acciones">
            <button type="submit" className="btn btn--primary">Conceder</button>
          </div>
        </form>
      )}
    </section>
  )
}
